import { motion } from "framer-motion";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { WaChatFrame, WaBubbleReceived } from "./WhatsAppMockup";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: Math.min(i, 6) * 0.08 } }),
};

const PROGRESS = [
  { label: "Héroes", icon: "🦸", pct: 96 },
  { label: "Mascotas", icon: "🐾", pct: 90 },
  { label: "Tropas", icon: "⚔️", pct: 98 },
  { label: "Hechizos", icon: "🔮", pct: 94 },
  { label: "Equipamientos", icon: "🛡️", pct: 88 },
];

function ProgressBars() {
  return (
    <div className="mt-2 space-y-1.5" data-testid="join-log-progress-bars">
      {PROGRESS.map((p) => (
        <div key={p.label} className="flex items-center gap-2 text-[11px] sm:text-xs">
          <span className="w-[92px] sm:w-[104px] shrink-0 text-wa-subtext">{p.icon} {p.label}</span>
          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-gold to-emerald-400" style={{ width: `${p.pct}%` }} />
          </div>
          <span className="w-9 text-right font-mono text-wa-text">{p.pct}%</span>
        </div>
      ))}
    </div>
  );
}

// Wrapper genérico para cada tarjeta de aviso: icono + título + subtítulo + frame de chat.
function AlertCard({ testid, icon, title, subtitle, delay, children, className = "" }) {
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={delay} variants={fade}
      className={`card-surface p-4 sm:p-5 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-1">
        <span className="text-2xl">{icon}</span>
        <div>
          <h3 className="font-heading font-semibold text-base sm:text-lg">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <WaChatFrame contactName="Imperio Hispano - Oficial" subtitle="ᵁᴺᴿ⚝ WARDEN · en línea" className="mt-4">
        {children}
      </WaChatFrame>
    </motion.div>
  );
}

function JoinLogCard() {
  return (
    <AlertCard testid="log-ingreso-salida-card" icon="📥" delay={0}
      title="Log de Ingreso / Salida de Miembros"
      subtitle="Ficha militar completa al instante, sin abrir el juego">
      <WaBubbleReceived
        testid="wa-bubble-join-header"
        text={
          "👋 **Nuevo miembro en Imperio Hispano**\n━━━━━━━━━━━━━━━━━━━━━━\n👤 **KingArthur_CoC**  `#92VY80QQ`\n🏛️ TH16   ⭐ Nivel 245\n🏆 Trofeos: 5,912 (récord 5,940)\n⚔️ Estrellas de guerra: 1,845\n🏅 Liga: Legend League"
        }
      />
      <div className="flex justify-start">
        <div className="relative bubble-tail-received bg-wa-received text-wa-text rounded-lg rounded-tl-none px-3.5 py-3 max-w-[92%] text-xs sm:text-sm shadow-md">
          <div className="font-mono text-wa-text mb-1">📊 <strong>Progreso basado en la API:</strong> 96%</div>
          <ProgressBars />
          <div className="text-[10px] text-wa-subtext mt-2">📌 Basado en datos visibles de la API oficial de Supercell.</div>
          <div className="text-right text-[10px] text-white/40 mt-1.5">14:32 ✓✓</div>
        </div>
      </div>
    </AlertCard>
  );
}

function WarReminderCard() {
  return (
    <AlertCard testid="alertas-guerra-card" icon="⏳" delay={1}
      title="Recordatorio de Ataques Pendientes"
      subtitle="Nadie vuelve a olvidar un ataque, con tiempo restante en vivo">
      <div className="flex justify-start">
        <div className="relative bubble-tail-received rounded-lg rounded-tl-none px-3.5 py-3 max-w-[94%] text-xs sm:text-sm shadow-md border-l-2 border-crimson bg-wa-received">
          <div className="font-mono">
            <div className="text-gold font-bold mb-1">⏳ ¡La Guerra termina en ~2h!</div>
            <div className="border-t border-wa-subtext/20 my-1.5" />
            <div className="text-wa-text">⚔️ Imperio Hispano vs Dragones del Norte</div>
            <div className="mt-2 text-crimson font-bold">🔴 Ataques pendientes (4):</div>
            <div className="mt-1 flex flex-col gap-1">
              {[["Carlos_CoC", "TH15", 2], ["ShadowNinja", "TH14", 1], ["Valquiria_Top", "TH16", 1]].map(([n, th, f]) => (
                <div key={n} className="flex items-center gap-1.5 text-wa-text">
                  <span className="text-[11px] bg-wa-accent/15 text-wa-accent px-1.5 py-0.5 rounded-full font-semibold">@{n}</span>
                  <span className="text-wa-subtext text-[11px]">({th}) — faltan {f}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 text-wa-text">¡No dejen ataques sin usar! 🗡️</div>
          </div>
          <div className="text-right text-[10px] text-white/40 mt-1.5">14:32</div>
        </div>
      </div>
    </AlertCard>
  );
}

function WarMatchCard() {
  return (
    <AlertCard testid="war-match-card" icon="🤝" delay={2}
      title="Emparejamiento de Guerra"
      subtitle="Aviso inmediato en cuanto el clan entra a búsqueda y se confirma el rival">
      <WaBubbleReceived
        testid="wa-bubble-war-searching"
        text={"🔎 **Buscando rival de guerra...**\n━━━━━━━━━━━━━━━━━━━━━━\n🟢 Imperio Hispano\n👥 Preparando alineación de 15 miembros.\n\n⏳ Avisaré en cuanto Supercell confirme el emparejamiento."}
      />
      <WaBubbleReceived
        testid="wa-bubble-war-matched"
        text={"🤝 **¡Se emparejó la Guerra!**\n━━━━━━━━━━━━━━━━━━━━━━\n🟢 Imperio Hispano\n🆚\n🔴 Dragones del Norte (Nv. 17)\n\n👥 15v15\n🛡️ Estamos en **preparación**. ¡A revisar castillos y planear el ataque!"}
      />
    </AlertCard>
  );
}

function CastleReminderCard() {
  return (
    <AlertCard testid="castle-reminder-card" icon="🏰" delay={3}
      title="Recordatorio de Llenar Castillo"
      subtitle="Revisen el castillo de guerra antes de que inicie, con análisis de composición TH">
      <WaBubbleReceived
        testid="wa-bubble-castle-reminder"
        text={
          "🏰 **Recordatorio de Castillos — Guerra**\n━━━━━━━━━━━━━━━━━━━━━━\n⚔️ Imperio Hispano vs Dragones del Norte\n🎯 **Rival:** Dragones del Norte (Nv. 17)\n⚖️ **TH nosotros vs rival:**\n  TH16: 8 vs 7\n  TH15: 5 vs 6\n⚠️ **Posible desventaja:** el rival tiene 6 TH15 y ustedes 5.\n⏳ La guerra inicia en ~1h 45m.\n\n📌 **Verifiquen en el juego, manualmente, si TODOS los castillos de guerra están llenos.** 🛡️\nRevisen base por base y llenen los que falten antes de que empiece la guerra.\n\n👥 **Alineación (15):**\n  #1 KingArthur_CoC (TH16)\n  #2 Valquiria_Top (TH16)\n  #3 Carlos_CoC (TH15)\n  ... (12 más)\n\nℹ️ La API de Clash of Clans no informa el contenido de los castillos, por eso la revisión debe hacerse dentro del juego."
        }
      />
    </AlertCard>
  );
}

function WarStartEndCard() {
  return (
    <AlertCard testid="war-start-end-card" icon="⚔️" delay={4}
      title="Inicio y Fin de Guerra"
      subtitle="Countdown al comenzar, resultado ganó/perdió/empató al finalizar">
      <WaBubbleReceived
        testid="wa-bubble-war-started"
        text={"⚔️ **¡Comenzó la Guerra!**\n━━━━━━━━━━━━━━━━━━━━━━\n🟢 Imperio Hispano\n🆚\n🔴 Dragones del Norte\n\n👥 15v15\n⏳ Termina en: 23h 58m\n\n¡A por la victoria! 🔥"}
      />
      <WaBubbleReceived
        testid="wa-bubble-war-ended"
        text={"🏁 **Finalizó la Guerra**\n━━━━━━━━━━━━━━━━━━━━━━\n🏆 **¡VICTORIA!**\n\n🟢 **Imperio Hispano**\n   ⭐ 42   💥 94.2%   🗡️ 28/30\n\n🔴 **Dragones del Norte**\n   ⭐ 39   💥 88.5%   🗡️ 26/30\n\n✅ **¡Todos completaron sus ataques!** 🎉"}
      />
    </AlertCard>
  );
}

function CwlLifecycleCard() {
  return (
    <AlertCard testid="cwl-lifecycle-card" icon="🏆" delay={5}
      title="CWL: Emparejamiento, Inicio y Fin"
      subtitle="Mismos avisos que la guerra normal, por cada ronda, más el MVP final"
    >
      <WaBubbleReceived
        testid="wa-bubble-cwl-matched"
        text={"🤝 **¡Se emparejó la liga CWL 🏆 - CAMPEONES 1!**\n━━━━━━━━━━━━━━━━━━━━━━\nComenzó la **Liga de Guerra de Clanes**. 🔥\nAntes de cada ronda te avisaré el rival y el recordatorio de castillos."}
      />
      <WaBubbleReceived
        testid="wa-bubble-cwl-round-ended"
        text={"🏁 **Finalizó la Batalla de CWL 🏆 - CAMPEONES 1**\n━━━━━━━━━━━━━━━━━━━━━━\n🏆 **¡VICTORIA!**\n\n🟢 Imperio Hispano   ⭐ 15   💥 98%   🗡️ 15/15\n🔴 Titanes de Acero   ⭐ 12   💥 85%   🗡️ 14/15\n\n✅ **¡Todos completaron sus ataques!** 🎉"}
      />
      <div className="flex justify-start" data-testid="wa-bubble-cwl-mvp">
        <div className="relative bubble-tail-received bg-wa-received text-wa-text rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[92%] text-xs sm:text-sm shadow-md">
          <div className="font-mono text-gold text-[11px] mb-1">📷 Imagen: Resumen Final de CWL</div>
          <div className="font-mono">
            🏅 Posición final: <strong className="text-white">#1</strong> de 8<br/>
            ⭐ Estrellas totales: <strong className="text-white">156</strong>&nbsp;·&nbsp;💥 92%<br/>
            🌟 <strong className="text-white">MVP: KingArthur_CoC</strong> (TH16) — 15⭐ en 5 ataques<br/>
            👑 Rey del pleno: KingArthur_CoC — 5 ataques de 3⭐
          </div>
          <div className="text-right text-[10px] text-white/40 mt-1.5">14:32</div>
        </div>
      </div>
    </AlertCard>
  );
}

function LeagueChangeCard() {
  return (
    <AlertCard testid="league-change-card" icon="📈" delay={6}
      title="Cambio de Liga Leyenda"
      subtitle="Aviso automático al grupo cuando un miembro asciende o desciende de tier">
      <WaBubbleReceived
        testid="wa-bubble-league-up"
        text={"🏆 **Cambio de Liga Leyenda**\n⬆️ ¡Ascenso!\nKingArthur_CoC pasó de **Legend II** a **Legend I**."}
      />
      <WaBubbleReceived
        testid="wa-bubble-league-down"
        text={"🏆 **Cambio de Liga Leyenda**\n⬇️ Descenso\nNocturno77 pasó de **Legend I** a **Legend II**."}
      />
    </AlertCard>
  );
}

function AttackSummaryCard() {
  return (
    <AlertCard testid="attack-summary-card" icon="🗡️" delay={7}
      title="Aviso por Cada Ataque y Defensa"
      subtitle="Estrellas y % de destrucción al instante, en cada golpe del clan o del rival">
      <WaBubbleReceived
        testid="wa-bubble-attack-notice"
        text={"⚔️ **Nuevo ataque**\nImperio Hispano vs Dragones del Norte\n⭐ Equipo: 28  |  Rival: 24\n⏳ Tiempo: 18h 32m\n\n🟢 #3 **KingArthur_CoC** → #9 Warlord_X\n   ⭐⭐⭐ (100%) ➔ 🆕 +3★\n\n🔥 ¡Ataque perfecto! Destrucción total. 🏆"}
      />
      <WaBubbleReceived
        testid="wa-bubble-defense-notice"
        text={"⚔️ **Nuevo ataque**\nImperio Hispano vs Dragones del Norte\n⭐ Equipo: 28  |  Rival: 25\n⏳ Tiempo: 18h 10m\n\n🔴 #7 **Warlord_X** → #4 Valquiria_Top\n   ⭐⭐☆ (81%) ➔ 🆕 +2★"}
      />
    </AlertCard>
  );
}

function MembershipCard() {
  return (
    <AlertCard testid="membership-notice-card" icon="🔔" delay={8} className="lg:col-span-2 lg:max-w-2xl lg:mx-auto lg:w-full"
      title="Aviso de Vencimiento de Membresía"
      subtitle="Recordatorio automático al grupo antes y al momento de vencer el plan">
      <WaBubbleReceived
        testid="wa-bubble-membership-upcoming"
        text={"⚠️ La membresía de este grupo vence en 7 días, el 22/09/2026. Para renovarla, contacta a: Carlos Admin (+52 123 456 7890)."}
      />
      <WaBubbleReceived
        testid="wa-bubble-membership-expired"
        text={"🚫 La membresía de este grupo expiró el 22/09/2026. Para renovarla, contacta a: Carlos Admin (+52 123 456 7890)."}
      />
    </AlertCard>
  );
}

export default function AutomatedAlerts() {
  return (
    <section id="alertas" className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="whatsapp-alerts-section">
      <div className="text-center max-w-2xl mx-auto mb-4">
        <span className="overline text-emerald-400">Avisos automáticos en tiempo real</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Tu clan avisado, sin que nadie tenga que preguntar</h2>
        <p className="text-muted-foreground mt-3">
          Alertas inteligentes que trabajan en segundo plano dentro del chat de WhatsApp de tu clan — el mismo lugar donde tu comunidad ya vive todos los días.
        </p>
      </div>
      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mx-auto max-w-fit mb-12">
        <ShieldCheck size={15} /> 100% dentro de WhatsApp — sin apps nuevas, sin Discord, sin abrir una PC.
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <JoinLogCard />
        <WarReminderCard />
        <WarMatchCard />
        <CastleReminderCard />
        <WarStartEndCard />
        <CwlLifecycleCard />
        <LeagueChangeCard />
        <AttackSummaryCard />
        <MembershipCard />
      </div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={9}
        className="mt-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <MessageCircle size={14} className="text-gold" /> Ejemplo ilustrativo con datos ficticios. El bot real usa los datos oficiales en vivo de tu clan.
      </motion.div>
    </section>
  );
}
